/**
 * **The grapple drag** — the post-move recomputer that makes the grapple's movement half
 * real: on the DOMINANT party's voluntary `move_token`, the bound partner is displaced by
 * the same `from → to` vector ("mirroring their movement," RAW Core p.69), clamped to a
 * legal cell, with an adjacency-restoring fallback so a clamp artifact doesn't hand the
 * defender a free break.
 *
 * **Layered semantics: strict mirror → footprint clamp → adjacency-restore → honest end.**
 *
 * 1. **Strict mirror.** `ideal = world.translate(partnerAnchor, from, moverTo)` — the
 *    draggee's pre-move anchor displaced by the mover's own `from → to` vector (the
 *    `translate` port primitive). In open ground this preserves the pair's relative offset
 *    exactly, so adjacency holds by construction.
 * 2. **Footprint clamp.** Walk `world.line(partnerAnchor → ideal)` (skipping the start
 *    cell) with the SAME walk-and-backoff loop {@link "./effects/spatialAnswerer".pushDestination}
 *    (Knockback) uses — {@link "./effects/spatialAnswerer".walkAndBackoff}, factored out
 *    so the two forced-move geometries can't drift. Occupancy is classified relative to
 *    the DRAGGEE (the partner), not the mover: a `blocked` cell (an equal-or-greater
 *    hostile, a genuinely-blocking object, terrain-solid) stops the walk outright;
 *    `noStop` cells (allies, strictly-smaller hostiles) are traversable but not
 *    endable, so the walk backs off to the last footprint-legal endpoint.
 * 3. **Adjacency-restore fallback.** If the clamped landing is NOT footprint-adjacent to
 *    the mover's NEW footprint, search every legal endpoint cell that IS
 *    footprint-adjacent to it and pick the one closest to the mirror-`ideal` cell,
 *    tie-broken by `world.key` ascending — DETERMINISTIC, so replay reproduces the same
 *    pick byte-for-byte (test-pinned).
 * 4. **Honest end.** Only when NO adjacency-restoring legal cell exists (a fully walled
 *    pocket) does the draggee stay behind — this recomputer emits NO step, and
 *    `grappleRecomputer` (next in the chain) then honestly ends the
 *    grapple; `grappleDominanceSyncRecomputer` (after that) clears the orphaned
 *    Immobilized/Dominance markers in the SAME chain.
 *
 * **No recursion.** The emitted `forced_move` step applies through `apply_resolved`
 * (`recomputeSpatial` in `backend/.../encounterRoom.ts`) — it never re-enters the
 * `move_token` command / hook chain, so a drag can never trigger another drag.
 *
 * **Two-undo shape.** The drag lands as its own eager-logged `apply_resolved` (kind
 * `spatial_drag`, `recomputeSpatial`'s naming convention), a SEPARATE log entry from the
 * mover's own `move_token`. Undoing a drag-move fully back is therefore TWO undos (the
 * drag entry, then the move entry) — the same LIFO discipline the wreck-conversion batch
 * documents. Don't "fix" this into one entry by mutating outside the log.
 *
 * **Inherited staleness gap.** Same as `engagementRecomputer`/`grappleRecomputer`/
 * `argonautShieldRecomputer`: this fires on `move_token` only. `place_token` (GM
 * force-relocation) and `forced_move` (an external push) of the DOMINANT party leave the
 * pair un-dragged until that party's next `move_token` — a known, unsolved gap.
 * Concretely: `ctx.from` is `undefined` for both (`MoveContext.from`'s doc comment), and
 * this recomputer's very first check treats that as "no step."
 */

import {
  type Cell,
  type EncounterState,
  type ResolvedStep,
  encounterGridMode,
  endpointFitterIn,
  isRooted,
  movementContext,
  towedPartnerOf,
  tagCell,
  worldFor,
} from "@lancer-vtt/domain";
import { type MovementContext, type Region, type World, cellRegion } from "@lancer-vtt/space";
import { nearestAdjacentLandings, walkAndBackoff } from "./effects/spatialAnswerer";
import { grappleDominantOf, grapplePartner } from "./grapple";
import type { MoveContext, SpatialRecomputer } from "./spatialRecompute";

/** Is the draggee's footprint anchored at `anchor` footprint-adjacent (distance 1) to `moverFp`? */
function isFootprintAdjacent(args: { world: World<Cell>; moverFp: Region<Cell>; anchor: Cell; size: number }): boolean {
  const { world, moverFp, anchor, size } = args;
  const draggeeFp = cellRegion(world.footprint(anchor, size).cells(), world.key);
  return world.regionDistance(moverFp, draggeeFp) === 1;
}

/**
 * The adjacency-restore fallback picker (step 3 above): the legal endpoint footprint-adjacent to
 * the mover's new footprint that is closest to the mirror-`ideal` cell, ties broken by `world.key`
 * ascending so the pick is deterministic across replay (test-pinned). `null` when no such cell
 * exists (the walled-pocket case — the caller leaves the draggee behind and lets
 * `grappleRecomputer` end the grapple honestly). The search is
 * {@link nearestAdjacentLandings}, shared with `place_adjacent`.
 */
function restoreAdjacency(args: {
  world: World<Cell>;
  moverAnchor: Cell;
  moverSize: number;
  draggeeSize: number;
  ideal: Cell;
  endpointLegal: (anchor: Cell) => boolean;
}): Cell | null {
  const { world, moverAnchor, moverSize, draggeeSize, ideal, endpointLegal } = args;
  return nearestAdjacentLandings({ world, anchor: moverAnchor, anchorSize: moverSize, draggeeSize, near: ideal, endpointLegal })[0] ?? null;
}

/** `ctx` with the mover's footprint at `mover.pos` moved from `blocked` into `noStop`. */
function carriedThroughMover(world: World<Cell>, ctx: MovementContext<Cell>, mover: { pos: Cell; size: number }): MovementContext<Cell> {
  const blocked = new Set(ctx.blocked);
  const noStop = new Set(ctx.noStop);
  for (const cell of world.footprint(mover.pos, mover.size).cells()) {
    const key = world.key(cell);
    blocked.delete(key);
    noStop.add(key);
  }
  return { ...ctx, blocked, noStop };
}

/** See the module doc comment above for the full algorithm + the four layered fallbacks. */
export const dragRecomputer: SpatialRecomputer = {
  id: "drag",
  recompute(ctx: MoveContext): ResolvedStep[] {
    const { state, moved, from } = ctx;
    // `place_token`/`forced_move` never set `from` (both ride this same chain with `from`
    // absent — see the module doc comment) — correctly a no-op here: a placement/knockback has
    // no path, so there is no mid-path drag mirror to compute. Only a real `move_token`, where
    // the room always threads `from`, drags a partner.
    if (from === undefined) return [];

    const mover = state.combatants[moved];
    if (!mover?.pos) return [];

    // A cable winch tow (`ll1-features` 9.4): the towed end follows the tower's move by the same
    // strict mirror and clamp, with no adjacency to restore (the cable keeps their distance).
    const towed = towedPartnerOf(state, mover);
    const grappled = grapplePartner(state, moved);
    const partnerId = towed?.id ?? grappled;
    if (partnerId === undefined) return [];
    const partner = state.combatants[partnerId];
    if (!partner?.pos) return []; // no resolvable, PLACED partner — nothing to drag.
    if (isRooted(partner)) return []; // a rooted map asset never moves; `grappleRecomputer` then ends the grapple on broken adjacency.

    // Only the DOMINANT party's move drags its bound grapple partner.
    if (towed === undefined && grappleDominantOf(state, moved, partnerId) !== moved) return [];

    const world = worldFor(state);
    const moverTo = mover.pos; // `state` is the POST-move state.
    if (world.equals(from, moverTo)) return []; // no actual displacement — nothing to mirror.

    // Layer 1: strict mirror — the draggee's pre-move anchor, displaced by the SAME
    // from→to vector the mover just moved (the `translate` primitive).
    const ideal = world.translate(partner.pos, from, moverTo);

    // Layer 2: footprint clamp — walk the straight line toward `ideal`, occupancy
    // classified relative to the DRAGGEE (not the mover), backing off to the last
    // footprint-legal endpoint. Shares the exact loop `pushDestination` (Knockback) uses.
    // The mover's own new footprint is traversable but not endable for its partner: a grappler that
    // passed over its partner carries it through, rather than its body clamping the drag at the
    // start (`projects/grapple-move-feel.md` Defect 3).
    const dragCtx = carriedThroughMover(world, movementContext(world, state, partnerId), { pos: moverTo, size: mover.base.size });
    const fitArgs = { space: world, size: partner.base.size, blocked: dragCtx.blocked, inBounds: dragCtx.inBounds };
    const endpointLegal = endpointFitterIn(world, dragCtx, partner.base.size);
    const rayCells = world.line(partner.pos, ideal).slice(1); // skip the start cell.
    const clamped = walkAndBackoff({
      world,
      fitArgs,
      endpointLegal,
      start: partner.pos,
      maxSteps: rayCells.length,
      next: (_cur, i) => rayCells[i] ?? null,
    });

    const moverFp = cellRegion(world.footprint(moverTo, mover.base.size).cells(), world.key);
    const landing = towed !== undefined
      ? clamped
      : isFootprintAdjacent({ world, moverFp, anchor: clamped, size: partner.base.size })
      ? clamped
      : // Layer 3: adjacency-restore — the clamp broke adjacency; find the legal cell
        // closest to `ideal` that keeps it.
        restoreAdjacency({
          world,
          moverAnchor: moverTo,
          moverSize: mover.base.size,
          draggeeSize: partner.base.size,
          ideal,
          endpointLegal,
        });

    // Layer 4: honest end — a fully walled pocket. Emit NO step; `grappleRecomputer`
    // (next in the chain) sees the still-broken adjacency and ends the grapple.
    if (landing === null) return [];
    if (world.equals(landing, partner.pos)) return []; // no net displacement — nothing to record.

    return [
      {
        op: "forced_move",
        target: partnerId,
        from: partner.pos,
        to: tagCell(encounterGridMode(state), landing),
        distance: world.distance(partner.pos, landing),
      },
    ];
  },
};
